import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { Empty, Input, Tooltip } from 'antd'
import { Pencil, Trash2, RefreshCw, Copy } from 'lucide-react'
import ClipLoader from 'react-spinners/ClipLoader'
import AllProductsStyle from '../AllProducts/AllProducts'
import Layout from '../../components/Layout'
import Button from '../../components/Button'
import { ErrorModal, SuccessModal } from '../../components/Modal'
import { BG_COLOR } from '../../constants/colors'
import { branchLink } from './branchLink'
import { copyText } from './clipboard'
import {
	createBranch,
	createInstallmentSetting,
	deleteBranch,
	deleteInstallmentSetting,
	getBranches,
	getInstallmentSettings,
	updateBranch,
	updateInstallmentSetting,
} from './api'
import { getErrorMessage, isConflict, isNotFound, isUnauthorized } from './errors'
import { formatRate, settingsCountLabel } from './constants'
import BranchModal from './BranchModal'
import SettingModal from './SettingModal'
import ConfirmDeleteModal from './ConfirmDeleteModal'

const BRANCH_QUERY = 'branchId'


const NasiyaSettings = () => {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const { getMe } = useSelector((state) => state.main)

	const jobTitle = get(getMe, 'U_JobTitle', '').trim().toLowerCase()
	const hasAccess = ['admin', 'kassir'].includes(jobTitle)


	const canEdit = hasAccess

	const errorRef = useRef()
	const successRef = useRef()

	const [branches, setBranches] = useState([])
	const [settings, setSettings] = useState([])
	const [isLoading, setIsLoading] = useState(false)
	const [search, setSearch] = useState('')

	const selectedBranchId = searchParams.get(BRANCH_QUERY) || ''

	const [branchModal, setBranchModal] = useState({ open: false, branch: null })
	const [settingModal, setSettingModal] = useState({
		open: false,
		setting: null,
	})
	const [deleteModal, setDeleteModal] = useState({
		open: false,
		type: '',
		item: null,
		count: 0,
	})

	const showError = (err) => {
		if (isUnauthorized(err)) {
			navigate('/login', { replace: true })
			return
		}
		errorRef.current?.open(getErrorMessage(err))
	}

	const loadAll = useCallback(async () => {
		setIsLoading(true)
		try {
			const [b, s] = await Promise.all([
				getBranches(),
				getInstallmentSettings(),
			])
			setBranches(b)
			setSettings(s)
		} catch (err) {
			showError(err)
		} finally {
			setIsLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (hasAccess) loadAll()
	}, [hasAccess, loadAll])

	/* ================= Hisoblangan qiymatlar ================= */

	const countByBranch = useMemo(() => {
		const map = {}
		settings.forEach((s) => {
			map[s.branchId] = (map[s.branchId] || 0) + 1
		})
		return map
	}, [settings])

	const branchName = useCallback(
		(id) => branches.find((b) => b.id === id)?.name || '—',
		[branches],
	)

	const filteredBranches = useMemo(() => {
		const q = search.trim().toLowerCase()
		if (!q) return branches
		return branches.filter((b) => (b.name || '').toLowerCase().includes(q))
	}, [branches, search])

	const selectedBranch = useMemo(
		() => branches.find((b) => b.id === selectedBranchId) || null,
		[branches, selectedBranchId],
	)

	// Tanlangan filial sozlamalari (yoki hammasi) — filial, keyin oylar bo'yicha
	const visibleSettings = useMemo(() => {
		const list = selectedBranchId
			? settings.filter((s) => s.branchId === selectedBranchId)
			: settings
		return [...list].sort((a, b) => {
			const byBranch = branchName(a.branchId).localeCompare(
				branchName(b.branchId),
			)
			return byBranch !== 0 ? byBranch : a.monthCount - b.monthCount
		})
	}, [settings, selectedBranchId, branchName])

	// URL dagi filial ro'yxatda yo'q bo'lsa (o'chirilgan) — filtrni tozalaymiz
	useEffect(() => {
		if (!isLoading && selectedBranchId && branches.length && !selectedBranch) {
			setSearchParams({}, { replace: true })
		}
	}, [isLoading, selectedBranchId, branches, selectedBranch, setSearchParams])

	const selectBranch = (id) => {
		if (id) setSearchParams({ [BRANCH_QUERY]: id })
		else setSearchParams({})
	}

	/* ================= Xatolik siyosati ================= */

	// 404 / 409 — boshqa admin o'zgartirgan: ro'yxatni yangilab, xabar ko'rsatamiz.
	// Qaytadigan qiymat: modalni yopish kerakmi (true) yoki forma qolsinmi (false).
	const handleMutationError = (err) => {
		if (isUnauthorized(err)) {
			navigate('/login', { replace: true })
			return true
		}
		errorRef.current?.open(getErrorMessage(err))
		if (isNotFound(err)) {
			loadAll()
			return true
		}
		if (isConflict(err)) {
			loadAll()
			return false
		}
		// 5xx / 403 / tarmoq — formani tozalamaymiz
		return false
	}

	/* ================= Filial amallari ================= */

	const submitBranch = async (name) => {
		const { branch } = branchModal
		try {
			if (branch) {
				const updated = await updateBranch(branch.id, name)
				setBranches((prev) =>
					prev
						.map((b) => (b.id === branch.id ? { ...b, ...updated } : b))
						.sort((a, b) => a.name.localeCompare(b.name)),
				)
			} else {
				const created = await createBranch(name)
				setBranches((prev) =>
					[...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
				)
			}
			setBranchModal({ open: false, branch: null })
			successRef.current?.open(
				branch ? 'Filial nomi o‘zgartirildi' : 'Filial qo‘shildi',
			)
			return true
		} catch (err) {
			const close = handleMutationError(err)
			if (close) setBranchModal({ open: false, branch: null })
			return close
		}
	}

	const copyBranchLink = async (branch) => {
		const link = branchLink(branch.name)
		try {
			await copyText(link)
			successRef.current?.open(`Nusxalandi ✓ — ${link}`)
		} catch (err) {
			errorRef.current?.open('Havolani nusxalab bo‘lmadi')
		}
	}

	// O'chirishdan oldin nechta sozlama ketishini API dan olamiz (TZ 2.5)
	const askDeleteBranch = async (branch) => {
		let count = countByBranch[branch.id] || 0
		try {
			const list = await getInstallmentSettings(branch.id)
			count = list.length
		} catch (err) {
			if (isNotFound(err)) {
				errorRef.current?.open(getErrorMessage(err))
				loadAll()
				return
			}
			if (isUnauthorized(err)) {
				navigate('/login', { replace: true })
				return
			}
			// boshqa xato — yuklangan sondan foydalanamiz
		}
		setDeleteModal({ open: true, type: 'branch', item: branch, count })
	}

	const askDeleteSetting = (setting) =>
		setDeleteModal({ open: true, type: 'setting', item: setting, count: 0 })

	const confirmDelete = async () => {
		const { type, item } = deleteModal
		try {
			if (type === 'branch') {
				await deleteBranch(item.id)
				setBranches((prev) => prev.filter((b) => b.id !== item.id))
				// cascade — filial sozlamalari ham ketadi
				setSettings((prev) => prev.filter((s) => s.branchId !== item.id))
				if (selectedBranchId === item.id) setSearchParams({})
				successRef.current?.open('Filial o‘chirildi')
			} else {
				await deleteInstallmentSetting(item.id)
				setSettings((prev) => prev.filter((s) => s.id !== item.id))
				successRef.current?.open('Sozlama o‘chirildi')
			}
			setDeleteModal({ open: false, type: '', item: null, count: 0 })
			return true
		} catch (err) {
			const close = handleMutationError(err)
			if (close) setDeleteModal({ open: false, type: '', item: null, count: 0 })
			return close
		}
	}

	/* ================= Sozlama amallari ================= */

	const submitSetting = async (body) => {
		const { setting } = settingModal
		try {
			if (setting) {
				const updated = await updateInstallmentSetting(setting.id, body)
				setSettings((prev) =>
					prev.map((s) => (s.id === setting.id ? { ...s, ...updated } : s)),
				)
			} else {
				const created = await createInstallmentSetting(body)
				setSettings((prev) => [...prev, created])
			}
			setSettingModal({ open: false, setting: null })
			successRef.current?.open(
				setting ? 'Sozlama yangilandi' : 'Sozlama qo‘shildi',
			)
			return true
		} catch (err) {
			const close = handleMutationError(err)
			if (close) setSettingModal({ open: false, setting: null })
			return close
		}
	}

	/* ================= Render ================= */

	if (!hasAccess) {
		return (
			<Layout>
				<AllProductsStyle style={{ backgroundColor: BG_COLOR }}>
					<div className="container">
						<p className="productTitle">Ruxsat yo‘q</p>
						{/* Rolni ko'rsatamiz — «tugmalar yo'q» holatini tez aniqlash uchun */}
						<p className="text-center text-sm text-gray-500 mt-2">
							Bu sahifa Admin va Kassir uchun. Sizning rolingiz:{' '}
							<b>{get(getMe, 'U_JobTitle', '') || 'aniqlanmadi'}</b>
						</p>
					</div>
				</AllProductsStyle>
			</Layout>
		)
	}

	const deleteContent =
		deleteModal.type === 'branch' ? (
			<p>
				<b>{deleteModal.item?.name}</b> filiali va unga tegishli{' '}
				<b>{deleteModal.count} ta nasiya sozlamasi</b> o‘chiriladi. Bu amalni
				qaytarib bo‘lmaydi. Davom etasizmi?
			</p>
		) : (
			<p>
				<b>{branchName(deleteModal.item?.branchId)}</b> filialidagi{' '}
				<b>{deleteModal.item?.monthCount} oylik</b> sozlama (
				{formatRate(deleteModal.item?.rate)}) o‘chiriladi. Davom etasizmi?
			</p>
		)

	return (
		<Layout>
			<AllProductsStyle style={{ backgroundColor: BG_COLOR }}>
				<div className="container">
					<div className="flex items-center justify-between flex-wrap gap-4">
						<p className="productTitle !text-left">
							Filiallar va nasiya sozlamalari
						</p>
						<Tooltip title="Ro‘yxatni yangilash">
							<button
								type="button"
								className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-100"
								onClick={loadAll}
								disabled={isLoading}
							>
								<RefreshCw
									size={18}
									className={isLoading ? 'animate-spin' : ''}
								/>
							</button>
						</Tooltip>
					</div>

					{isLoading && branches.length === 0 ? (
						<ClipLoader
							loading={true}
							size={75}
							aria-label="Loading Spinner"
							className={'loader'}
							data-testid="loader"
						/>
					) : (
						<div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6 mt-6">
							{/* ================= Filiallar ================= */}
							<section className="bg-white rounded-xl drop-shadow-md p-4">
								<div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
									<h2 className="text-lg font-semibold">Filiallar</h2>
									{canEdit && (
										<Button
											className="!h-9 !px-3"
											onClick={() =>
												setBranchModal({ open: true, branch: null })
											}
										>
											+ Filial qo‘shish
										</Button>
									)}
								</div>

								<Input.Search
									allowClear
									placeholder="Filial nomi bo‘yicha qidirish"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="mb-3"
								/>

								{branches.length === 0 ? (
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description="Hali birorta filial qo‘shilmagan"
									>
										{canEdit && (
											<Button
												className="!h-9 !px-3 mx-auto"
												onClick={() =>
													setBranchModal({ open: true, branch: null })
												}
											>
												+ Filial qo‘shish
											</Button>
										)}
									</Empty>
								) : (
									<div className="overflow-x-auto">
										<table className="w-full text-sm text-left text-gray-600 min-w-[360px]">
											<thead className="text-xs text-gray-700 uppercase bg-gray-50">
												<tr>
													<th className="px-4 py-3">Filial nomi</th>
													<th className="px-4 py-3">Nasiya shartlari</th>
													{canEdit && (
														<th className="px-4 py-3 text-right">Amallar</th>
													)}
												</tr>
											</thead>
											<tbody>
												<tr
													className={`border-b cursor-pointer ${
														!selectedBranchId
															? 'bg-blue-50'
															: 'bg-white hover:bg-gray-50'
													}`}
													onClick={() => selectBranch('')}
												>
													<td className="px-4 py-3 font-medium" colSpan={2}>
														Barcha filiallar
													</td>
													{canEdit && <td />}
												</tr>
												{filteredBranches.length === 0 ? (
													<tr>
														<td colSpan={canEdit ? 3 : 2}>
															<Empty
																image={Empty.PRESENTED_IMAGE_SIMPLE}
																description="Hech narsa topilmadi"
															/>
														</td>
													</tr>
												) : (
													filteredBranches.map((b) => {
														const count = countByBranch[b.id] || 0
														const active = b.id === selectedBranchId
														return (
															<tr
																key={b.id}
																className={`border-b cursor-pointer ${
																	active
																		? 'bg-blue-50'
																		: 'bg-white hover:bg-gray-50'
																}`}
																onClick={() => selectBranch(b.id)}
															>
																<td className="px-4 py-3 font-medium text-blue-900">
																	{b.name}
																</td>
																<td className="px-4 py-3">
																	<span
																		className={
																			count > 0
																				? 'text-gray-700'
																				: 'text-gray-400'
																		}
																	>
																		{settingsCountLabel(count)}
																	</span>
																</td>
																{canEdit && (
																	<td className="px-4 py-3">
																		<div className="flex justify-end gap-2">
																			<Tooltip title="Havolani nusxalash">
																				<button
																					type="button"
																					className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
																					onClick={(e) => {
																						e.stopPropagation()
																						copyBranchLink(b)
																					}}
																				>
																					<Copy size={16} />
																				</button>
																			</Tooltip>
																			<Tooltip title="Tahrirlash">
																				<button
																					type="button"
																					className="p-1.5 rounded hover:bg-blue-100 text-blue-700"
																					onClick={(e) => {
																						e.stopPropagation()
																						setBranchModal({
																							open: true,
																							branch: b,
																						})
																					}}
																				>
																					<Pencil size={16} />
																				</button>
																			</Tooltip>
																			<Tooltip title="O‘chirish">
																				<button
																					type="button"
																					className="p-1.5 rounded hover:bg-red-100 text-red-600"
																					onClick={(e) => {
																						e.stopPropagation()
																						askDeleteBranch(b)
																					}}
																				>
																					<Trash2 size={16} />
																				</button>
																			</Tooltip>
																		</div>
																	</td>
																)}
															</tr>
														)
													})
												)}
											</tbody>
										</table>
									</div>
								)}
							</section>

							{/* ================= Nasiya sozlamalari ================= */}
							<section className="bg-white rounded-xl drop-shadow-md p-4">
								<div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
									<div>
										<h2 className="text-lg font-semibold">
											Nasiya sozlamalari
										</h2>
										<p className="text-sm text-gray-500">
											{selectedBranch
												? `${selectedBranch.name} filiali`
												: 'Barcha filiallar'}
											{' · '}
											{visibleSettings.length} ta
										</p>
									</div>
									{canEdit && (
										<Button
											className="!h-9 !px-3"
											disabled={branches.length === 0}
											onClick={() =>
												setSettingModal({ open: true, setting: null })
											}
										>
											+ Sozlama qo‘shish
										</Button>
									)}
								</div>

								{visibleSettings.length === 0 ? (
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description={
											branches.length === 0
												? 'Avval filial qo‘shing'
												: selectedBranch
													? `${selectedBranch.name} filialida nasiya shartlari sozlanmagan`
													: 'Hali birorta sozlama qo‘shilmagan'
										}
									/>
								) : (
									<div className="overflow-x-auto">
										<table className="w-full text-sm text-left text-gray-600 min-w-[360px]">
											<thead className="text-xs text-gray-700 uppercase bg-gray-50">
												<tr>
													{!selectedBranchId && (
														<th className="px-4 py-3">Filial</th>
													)}
													<th className="px-4 py-3">Oylar soni</th>
													<th className="px-4 py-3">Foiz</th>
													{canEdit && (
														<th className="px-4 py-3 text-right">Amallar</th>
													)}
												</tr>
											</thead>
											<tbody>
												{visibleSettings.map((s) => (
													<tr
														key={s.id}
														className="bg-white border-b hover:bg-gray-50"
													>
														{!selectedBranchId && (
															<td className="px-4 py-3 font-medium text-blue-900">
																{branchName(s.branchId)}
															</td>
														)}
														<td className="px-4 py-3">{s.monthCount} oy</td>
														<td className="px-4 py-3 font-semibold">
															{formatRate(s.rate)}
														</td>
														{canEdit && (
															<td className="px-4 py-3">
																<div className="flex justify-end gap-2">
																	<Tooltip title="Tahrirlash">
																		<button
																			type="button"
																			className="p-1.5 rounded hover:bg-blue-100 text-blue-700"
																			onClick={() =>
																				setSettingModal({
																					open: true,
																					setting: s,
																				})
																			}
																		>
																			<Pencil size={16} />
																		</button>
																	</Tooltip>
																	<Tooltip title="O‘chirish">
																		<button
																			type="button"
																			className="p-1.5 rounded hover:bg-red-100 text-red-600"
																			onClick={() => askDeleteSetting(s)}
																		>
																			<Trash2 size={16} />
																		</button>
																	</Tooltip>
																</div>
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</section>
						</div>
					)}
				</div>
			</AllProductsStyle>

			<BranchModal
				open={branchModal.open}
				branch={branchModal.branch}
				onSubmit={submitBranch}
				onCancel={() => setBranchModal({ open: false, branch: null })}
			/>
			<SettingModal
				open={settingModal.open}
				branches={branches}
				settings={settings}
				setting={settingModal.setting}
				defaultBranchId={selectedBranchId}
				onSubmit={submitSetting}
				onCancel={() => setSettingModal({ open: false, setting: null })}
			/>
			<ConfirmDeleteModal
				open={deleteModal.open}
				title={
					deleteModal.type === 'branch'
						? 'Filialni o‘chirish'
						: 'Sozlamani o‘chirish'
				}
				content={deleteContent}
				onConfirm={confirmDelete}
				onCancel={() =>
					setDeleteModal({ open: false, type: '', item: null, count: 0 })
				}
			/>

			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
			<SuccessModal
				getRef={(r) => {
					successRef.current = r
				}}
			/>
		</Layout>
	)
}

export default NasiyaSettings
